import React, {
  useState, useCallback, useEffect, memo, useRef,
} from 'react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import Form, { FormConsumer } from '@active/react-ui/Form';
import FormGroup from '@active/react-ui/FormGroup';
import FormField from '@active/react-ui/FormField';
import FormLabel from '@active/react-ui/FormLabel';
import { useLocale, Locale } from '@active/react-utils/i18n';
import CurrencyInput from '@active/react-ui/CurrencyInput';
import Currency from '../../../../../Currency';
import pubSubInstance from '../../../../../../../shared/utils/pubSub';
import useCurrentAmountAndCreditBalance from '../../../../../../../shared/utils/currentAmountAndCreditBalance/useCurrentAmountAndCreditBalance';
import { parseFloatData } from '../../../../../../../shared/utils/commonUtils';
import { usePennyRounding } from '../../../../../../../shared/utils/customizedHooks';

const Cash = () => {
  const { i18n } = useLocale();
  const paymentForm = useRef();
  const [ changeDue, setChangeDue ] = useState(null);
  const [ isShowWarning, setIsShowWarning ] = useState(false);
  const { yourPayment } = useCurrentAmountAndCreditBalance();
  const {
    entities: {
      orderInfo: {
        currencyCode,
      } = {},
    },
  } = useSelector(state => state.mainStateData);

  const {
    widgetNameSpace,
    isShowCashCalculator,
  } = useSelector(state => state.propsData);

  const {
    currentActivePaymentMethod,
    entities: {
      paymentMethods: {
        cashInfo: {
          pennyRounding,
        } = {},
      } = {},
    },
  } = useSelector(state => state.paymentMethods);

  const {
    pennyRoundingAmount,
    grandTotal,
  } = usePennyRounding([ yourPayment, currentActivePaymentMethod, pennyRounding, currencyCode ]);

  const handleValidate = useCallback(() => {
    return new Promise((resolve) => {
      paymentForm.current ? paymentForm.current.validateForm({ shouldTouchFields: true }).then((res) => {
        const formValid = isEmpty(res);
        resolve(formValid);
      }) : resolve(true);
    });
  }, [ paymentForm.current ]);

  const handleSubmit = useCallback(() => {
    return Promise.resolve({ status: true, paymentMethodType: 'CASH', pennyRoundingAmount });
  }, [ pennyRoundingAmount ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-validate`, handleValidate);
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-validate`, handleValidate);
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    };
  }, [ widgetNameSpace, handleValidate, handleSubmit ]);

  const handleBlur = useCallback((e) => {
    const inputValue = e.target.value;
    if (isNil(inputValue) || inputValue === '') {
      setChangeDue(null);
      setIsShowWarning(false);
      return;
    }
    const inputAmount = Number(inputValue);
    const paymentAmount = grandTotal !== undefined ? grandTotal : yourPayment; // should cover grandTotal is 0 case
    setIsShowWarning(inputAmount < paymentAmount);
    setChangeDue(parseFloatData(inputAmount - paymentAmount));
  }, [ setIsShowWarning, yourPayment, grandTotal ]);

  if (!isShowCashCalculator) {
    return null;
  }
  return <div className='payment-method-cash'>
    <Form customizeLayout className='cui-mg-t-20'>
      <FormGroup name="cashReceived">
        <FormLabel >
          <Locale id="checkout.lib.components.payment.cash.changeCalculator" />
        </FormLabel>
        <FormField
          codePosition="prefix"
          as={CurrencyInput}
          autoComplete="off"
          preText={currencyCode}
          code={currencyCode}
          onBlur={handleBlur}
          warning={isShowWarning ? i18n('checkout.lib.components.text.provideAmountGreater') : undefined}/>
      </FormGroup>
      <div className="payment-method-cash__change-due cui-mg-t-20">
        <Locale id="checkout.lib.components.payment.cash.changeDue"/>
        <div className="payment-method-cash__change-due-amount">
          { !isShowWarning && <Currency amount={changeDue} code={currencyCode}/> }
        </div>
      </div>
      <FormConsumer>
        {
          ({ form }) => {
            paymentForm.current = form;
          }
        }
      </FormConsumer>
    </Form>
  </div>;
};

export default memo(Cash);
